import { forwardRef } from "react";
import { styled } from "~/ui/style/stitches.config";
import { CardResult } from "~/api/dailySpreadApi";
import rwsDeck from "~/assets/decks/rider-waite.json";

interface ShareableSpreadProps {
  title: string;
  positions: string[];
  cards: CardResult[];
  interpretation: string | null;
  backgroundColor: string;
}

export const ShareableSpread = forwardRef<HTMLDivElement, ShareableSpreadProps>(
  ({ title, positions, cards, interpretation, backgroundColor }, ref) => {
    const numPositions = positions.length;
    // Scale cards for 540px wide canvas
    const cardWidth = numPositions >= 3 ? 120 : numPositions === 1 ? 200 : 160;
    const cardHeight = Math.round(cardWidth * 1.617);

    return (
      <ShareContainer ref={ref} style={{ backgroundColor }}>
        <ShareTitlePill>{title}</ShareTitlePill>
        <ShareCardContainer style={{ padding: numPositions === 1 ? "40px 100px" : "40px 36px" }}>
          <ShareCardsGrid
            style={{ gridTemplateColumns: `repeat(${numPositions}, auto)` }}
          >
            {positions.map((position, index) => (
              <SharePositionLabel key={`label-${index}`} style={{ width: cardWidth }}>
                {position}:
              </SharePositionLabel>
            ))}
            {cards.map((card, index) => (
              <ShareCardWrapper key={`card-${index}`}>
                <ShareCard
                  src={`/images${rwsDeck.basePath}/${card.artwork}`}
                  style={{
                    width: cardWidth,
                    height: cardHeight,
                    transform: card.reversed ? "rotate(180deg)" : "none",
                  }}
                  crossOrigin="anonymous"
                />
              </ShareCardWrapper>
            ))}
          </ShareCardsGrid>
        </ShareCardContainer>
        <ShareCredit>@entermoonlight</ShareCredit>
        {interpretation && (
          <ShareInterpretation>{interpretation}</ShareInterpretation>
        )}
      </ShareContainer>
    );
  }
);

ShareableSpread.displayName = "ShareableSpread";

// IG post size 4:5 = 540 x 675 (renders at 1080x1350 with scale: 2)
const ShareContainer = styled("div", {
  width: "540px",
  height: "675px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "40px 30px",
  gap: "25px",
  boxSizing: "border-box",
});

const ShareTitlePill = styled("div", {
  backgroundColor: "#1a1a1a",
  color: "#fff",
  padding: "9px 32px 11px",
  borderRadius: "30px",
  fontSize: "13px",
  fontWeight: "400",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  lineHeight: 1,
});

const ShareCardContainer = styled("div", {
  backgroundColor: "#f6f6f6",
  borderRadius: "24px",
  padding: "40px 36px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "20px",
  border: "1px solid #000",
});

const ShareCardsGrid = styled("div", {
  display: "grid",
  gap: "12px 24px",
  justifyItems: "center",
  alignItems: "end",
  width: "fit-content",
  margin: "0 auto",
});

const SharePositionLabel = styled("div", {
  fontSize: "18px",
  fontWeight: "500",
  lineHeight: "1.2",
  color: "#000",
  textAlign: "center",
});

const ShareCardWrapper = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const ShareCard = styled("img", {
  borderRadius: "8px",
  border: "1px solid rgba(0, 0, 0, 0.25)",
  objectFit: "cover",
});

const ShareInterpretation = styled("p", {
  fontSize: "13px",
  color: "#000",
  textAlign: "center",
  maxWidth: "450px",
  margin: "0",
  lineHeight: "1.5",
  fontWeight: "500",
  padding: "0 10px",
  textWrap: "balance",
});

const ShareCredit = styled("div", {
  fontSize: "12px",
  color: "#000",
  textAlign: "center",
  opacity: 0.9,
});

